import formData from 'form-data';
import Mailgun from 'mailgun.js';
import { Entry, Campaign } from '../types';
import { supabase } from '../lib/supabase';

// Get environment variables
const MAILGUN_API_KEY = import.meta.env.VITE_MAILGUN_API_KEY;
const MAILGUN_DOMAIN = import.meta.env.VITE_MAILGUN_DOMAIN;
const MAILGUN_FROM_EMAIL = import.meta.env.VITE_MAILGUN_FROM_EMAIL;

if (!MAILGUN_API_KEY || !MAILGUN_DOMAIN || !MAILGUN_FROM_EMAIL) {
  console.error('Missing required Mailgun environment variables');
  console.log('Current env:', {
    MAILGUN_API_KEY,
    MAILGUN_DOMAIN,
    MAILGUN_FROM_EMAIL
  });
}

const mailgun = new Mailgun(formData);
const mg = mailgun.client({
  username: 'api',
  key: MAILGUN_API_KEY || '',
  url: 'https://api.mailgun.net'
});

interface EmailOptions {
  to: string;
  subject: string;
  text: string;
  html?: string;
}

export async function sendEmail({ to, subject, text, html }: EmailOptions) {
  try {
    const data = {
      from: MAILGUN_FROM_EMAIL || 'Raffle System <noreply@yourdomain.com>',
      to,
      subject,
      text,
      html: html || text
    };

    const response = await mg.messages.create(
      MAILGUN_DOMAIN || '',
      data
    );

    // Log successful email
    const { error: logError } = await supabase.from('email_logs').insert({
      email: to,
      subject,
      status: 'sent',
      created_at: new Date().toISOString()
    });

    if (logError) {
      console.error('Error logging successful email:', logError);
    }

    return response;
  } catch (error) {
    console.error('Error sending email:', error);

    // Log failed email
    const { error: logError } = await supabase.from('email_logs').insert({
      email: to,
      subject,
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
      created_at: new Date().toISOString()
    });

    if (logError) {
      console.error('Error logging failed email:', logError);
    }

    throw error;
  }
}

// Email templates
export const emailTemplates = {
  entryConfirmation: (entries: Entry[], campaign: Campaign) => {
    const prizeName = campaign.prizes?.[0]?.name || campaign.name;
    return {
      subject: `Welcome to the raffle for ${prizeName}!`,
      text: `Welcome to the raffle for ${prizeName}!\n\n` +
            `We are grateful for your participation.\n` +
            `We wish you the best of luck—and we hope you'll be the winner!\n\n` +
            `Your ticket numbers are:\n${entries.map(entry => `- ${entry.ticket_number}`).join('\n')}\n\n` +
            `Entry details:\n` +
            `- Name: ${entries[0].name}\n` +
            `- Email: ${entries[0].email}\n` +
            `- Total Tickets: ${entries.length}\n` +
            `- Total Amount: $${entries.length * campaign.ticket_price}\n\n` +
            `The draw will take place on ${new Date(campaign.draw_date).toLocaleDateString()}.\n\n` +
            `Best regards,\nQRaffle Team`,
      html: `
        <h1>Welcome to the raffle for ${prizeName}!</h1>
        <p>We are grateful for your participation.</p>
        <p>We wish you the best of luck—and we hope you'll be the winner!</p>
        <p>Your ticket numbers are:</p>
        <ul>
          ${entries.map(entry => `<li><strong>${entry.ticket_number}</strong></li>`).join('')}
        </ul>
        <p>Entry details:</p>
        <ul>
          <li>Name: ${entries[0].name}</li>
          <li>Email: ${entries[0].email}</li>
          <li>Total Tickets: ${entries.length}</li>
          <li>Total Amount: $${entries.length * campaign.ticket_price}</li>
        </ul>
        <p>The draw will take place on ${new Date(campaign.draw_date).toLocaleDateString()}.</p>
        <p>Best regards,</p>
        <p>QRaffle Team</p>
      `
    };
  },

  paymentConfirmation: (ticketNumber: string) => ({
    subject: `Payment Confirmation`,
    text: `Your payment has been confirmed!\n\n` +
          `Ticket number: ${ticketNumber}\n\n` +
          `Good luck!\n\n` +
          `Best regards,\nQRaffle Team`,
    html: `
      <h1>Payment Confirmed!</h1>
      <p>Your payment has been confirmed!</p>
      <p>Ticket number: ${ticketNumber}</p>
      <p>Good luck!</p>
      <p>Best regards,</p>
      <p>QRaffle Team</p>
    `
  }),

  fullPaymentConfirmation: (entry: Entry, campaign: Campaign) => ({
    subject: `Payment Confirmation - ${campaign.name}`,
    text: `Your payment for the ${campaign.name} raffle has been confirmed!\n\n` +
          `Ticket number: ${entry.ticket_number}\n` +
          `Amount paid: $${campaign.ticket_price}\n\n` +
          `The draw will take place on ${new Date(campaign.draw_date).toLocaleDateString()}.\n` +
          `Good luck!\n\n` +
          `Best regards,\nQRaffle Team`,
    html: `
      <h1>Payment Confirmed!</h1>
      <p>Your payment for the ${campaign.name} raffle has been confirmed!</p>
      <p>Ticket number: ${entry.ticket_number}</p>
      <p>Amount paid: $${campaign.ticket_price}</p>
      <p>The draw will take place on ${new Date(campaign.draw_date).toLocaleDateString()}.</p>
      <p>Good luck!</p>
      <p>Best regards,</p>
      <p>QRaffle Team</p>
    `
  }),

  winnerAnnouncement: (name: string, prize: string) => ({
    subject: `🏆 Congratulations — You've Won the ${prize} Raffle!`,
    text: `Hi ${name},\n\n` +
          `🎉 Congratulations! You are the winner of the raffle for ${prize}!\n\n` +
          `We're thrilled to share this exciting news with you. Your entry has been randomly selected, and you've officially won.\n\n` +
          `Please get in touch with the organizer of the raffle to arrange delivery or pick-up.\n\n` +
          `Thank you for participating, and once again — congrats on your big win!\n\n` +
          `Best regards,\nQRaffle Team`,
    html: `
      <h1>🏆 Congratulations — You've Won the ${prize} Raffle!</h1>
      <p>Hi ${name},</p>
      <p>🎉 Congratulations! You are the winner of the raffle for ${prize}!</p>
      <p>We're thrilled to share this exciting news with you. Your entry has been randomly selected, and you've officially won.</p>
      <p>Please get in touch with the organizer of the raffle to arrange delivery or pick-up.</p>
      <p>Thank you for participating, and once again — congrats on your big win!</p>
      <p>Best regards,</p>
      <p>QRaffle Team</p>
    `
  }),

  preRaffleReminder: (name: string, campaignName: string, drawDate: string) => ({
    subject: `Reminder: ${campaignName} Draw Tomorrow`,
    text: `Hi ${name},\n\n` +
          `This is a reminder that the ${campaignName} raffle draw will take place on ${drawDate}!\n\n` +
          `Good luck!\n\n` +
          `Best regards,\nQRaffle Team`,
    html: `
      <h1>Raffle Draw Reminder</h1>
      <p>Hi ${name},</p>
      <p>This is a reminder that the ${campaignName} raffle draw will take place on ${drawDate}!</p>
      <p>Good luck!</p>
      <p>Best regards,</p>
      <p>QRaffle Team</p>
    `
  }),

  drawStarting: (name: string, campaignName: string, ticketNumber: string) => {
    return {
      subject: `🎉 The Raffle for ${campaignName} is Starting Now!`,
      text: `Hi ${name},\n\n` +
            `It's time! The raffle draw for ${campaignName} is starting now.\n\n` +
            `Your ticket number: ${ticketNumber}\n\n` +
            `We're excited to see who the lucky winner will be — and we're wishing you the best of luck!\n\n` +
            `Thank you for participating and being part of the fun.\n\n` +
            `Best regards,\nQRaffle Team`,
      html: `
        <h1>🎉 The Raffle for ${campaignName} is Starting Now!</h1>
        <p>Hi ${name},</p>
        <p>It's time! The raffle draw for ${campaignName} is starting now.</p>
        <p>Your ticket number: <strong>${ticketNumber}</strong></p>
        <p>We're excited to see who the lucky winner will be — and we're wishing you the best of luck!</p>
        <p>Thank you for participating and being part of the fun.</p>
        <p>Best regards,</p>
        <p>QRaffle Team</p>
      `
    };
  },

  preDrawReminder: (entries: Entry[], campaign: Campaign) => {
    const prizeName = campaign.prizes?.[0]?.name || campaign.name;
    const totalTickets = campaign.total_tickets;
    const participantTickets = entries.length;
    const winChance = ((participantTickets / totalTickets) * 100).toFixed(2);

    return {
      subject: `⏳ Your Chance to Win ${prizeName} — Raffle Draw is Almost Here!`,
      text: `Hi ${entries[0].name},\n\n` +
            `The raffle draw for ${prizeName} is happening soon — just a few more hours to go!\n\n` +
            `Your current chance of winning is ${participantTickets}/${totalTickets}, or ${winChance}%.\n` +
            `We're wishing you the best of luck!\n\n` +
            `Want to boost your chances?\n` +
            `You can still purchase more tickets before the draw closes:\n` +
            `👉 https://qraffle.com/campaign/${campaign.id}\n\n` +
            `Thank you for being a part of this exciting raffle!\n\n` +
            `Best regards,\nQRaffle Team`,
      html: `
        <h1>⏳ Your Chance to Win ${prizeName} — Raffle Draw is Almost Here!</h1>
        <p>Hi ${entries[0].name},</p>
        <p>The raffle draw for ${prizeName} is happening soon — just a few more hours to go!</p>
        <p>Your current chance of winning is <strong>${participantTickets}/${totalTickets}</strong>, or <strong>${winChance}%</strong>.</p>
        <p>We're wishing you the best of luck!</p>
        <p>Want to boost your chances?</p>
        <p>You can still purchase more tickets before the draw closes:</p>
        <p>👉 <a href="https://qraffle.com/campaign/${campaign.id}">Buy More Tickets</a></p>
        <p>Thank you for being a part of this exciting raffle!</p>
        <p>Best regards,</p>
        <p>QRaffle Team</p>
      `
    };
  },

  nonWinnerNotification: (entries: Entry[], campaign: Campaign) => {
    const prizeName = campaign.prizes?.[0]?.name || campaign.name;
    return {
      subject: `Thank You for Participating in the ${prizeName} Raffle 💙`,
      text: `Hi ${entries[0].name},\n\n` +
            `The raffle for ${prizeName} has officially ended, and while your entry wasn't the winning one this time, we want to sincerely thank you for participating!\n\n` +
            `We truly appreciate your support and hope you had fun being part of the excitement. There will be more raffles and more chances to win — so stay tuned!\n\n` +
            `In the meantime, keep an eye on your inbox for upcoming draws and exclusive opportunities.\n\n` +
            `Wishing you better luck next time 🍀\n\n` +
            `Best regards,\nQRaffle Team`,
      html: `
        <h1>Thank You for Participating in the ${prizeName} Raffle 💙</h1>
        <p>Hi ${entries[0].name},</p>
        <p>The raffle for ${prizeName} has officially ended, and while your entry wasn't the winning one this time, we want to sincerely thank you for participating!</p>
        <p>We truly appreciate your support and hope you had fun being part of the excitement. There will be more raffles and more chances to win — so stay tuned!</p>
        <p>In the meantime, keep an eye on your inbox for upcoming draws and exclusive opportunities.</p>
        <p>Wishing you better luck next time 🍀</p>
        <p>Best regards,</p>
        <p>QRaffle Team</p>
      `
    };
  },

  winnerReminder: (entry: Entry, campaign: Campaign) => {
    const prizeName = campaign.prizes?.[0]?.name || campaign.name;
    return {
      subject: `⏰ Reminder: Claim Your ${prizeName} — You're the Raffle Winner!`,
      text: `Hi ${entry.name},\n\n` +
            `Just a quick reminder — you were selected as the winner of the ${prizeName} raffle, but we haven't heard back from you yet.\n\n` +
            `It's been over 24 hours since the draw, and we want to make sure you don't miss out on your prize!\n\n` +
            `Please confirm and claim your prize as soon as possible by following the link below:\n` +
            `👉 https://qraffle.com/claim-prize/${campaign.id}/${entry.id}\n\n` +
            `If we don't hear back soon, we may need to move forward according to our raffle terms.\n\n` +
            `Congratulations again — we hope to hear from you shortly!\n\n` +
            `Best regards,\nQRaffle Team`,
      html: `
        <h1>⏰ Reminder: Claim Your ${prizeName} — You're the Raffle Winner!</h1>
        <p>Hi ${entry.name},</p>
        <p>Just a quick reminder — you were selected as the winner of the ${prizeName} raffle, but we haven't heard back from you yet.</p>
        <p>It's been over 24 hours since the draw, and we want to make sure you don't miss out on your prize!</p>
        <p>Please confirm and claim your prize as soon as possible by following the link below:</p>
        <p>👉 <a href="https://qraffle.com/claim-prize/${campaign.id}/${entry.id}">Claim Your Prize</a></p>
        <p>If we don't hear back soon, we may need to move forward according to our raffle terms.</p>
        <p>Congratulations again — we hope to hear from you shortly!</p>
        <p>Best regards,</p>
        <p>QRaffle Team</p>
      `
    };
  },

  redrawAnnouncement: (entries: Entry[], campaign: Campaign) => {
    const prizeName = campaign.prizes?.[0]?.name || campaign.name;
    return {
      subject: `🎯 No Winner Claimed — We're Drawing Again!`,
      text: `Hi ${entries[0].name},\n\n` +
            `The raffle for ${prizeName} was drawn 24 hours ago, but the winner has not claimed their prize.\n\n` +
            `As outlined in our raffle rules, we'll be conducting a new draw to ensure the prize finds a rightful home!\n\n` +
            `The redraw will take place shortly — and your existing tickets are still valid.\n` +
            `This means you're still in the running, and we're wishing you the best of luck in the second draw!\n\n` +
            `Thank you for being part of the QRaffle community!\n\n` +
            `Best regards,\nQRaffle Team`,
      html: `
        <h1>🎯 No Winner Claimed — We're Drawing Again!</h1>
        <p>Hi ${entries[0].name},</p>
        <p>The raffle for ${prizeName} was drawn 24 hours ago, but the winner has not claimed their prize.</p>
        <p>As outlined in our raffle rules, we'll be conducting a new draw to ensure the prize finds a rightful home!</p>
        <p>The redraw will take place shortly — and your existing tickets are still valid.</p>
        <p>This means you're still in the running, and we're wishing you the best of luck in the second draw!</p>
        <p>Thank you for being part of the QRaffle community!</p>
        <p>Best regards,</p>
        <p>QRaffle Team</p>
      `
    };
  }
};